import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  Lock,
  PlayCircle,
  X,
} from 'lucide-react';
import useScrollToTop from '../hooks/useScrollToTop';
import {
  useCourseApi,
  CourseDetails,
} from '../hooks/useCourseApi';
import UnitAccessTimer from '../components/common/UnitAccessTimer';
import { cardTheme } from '../styles/cardTheme';
import { mapCourseLoadError } from '../utils/courseLoadLabels';
import videoLessonIcon from '../assets/images/curriculum/video.png';
import quizLessonIcon from '../assets/images/curriculum/quiz.png';
import homeworkLessonIcon from '../assets/images/curriculum/homework.png';
import pdfLessonIcon from '../assets/images/curriculum/pdf.png';
import ConfiguredPracticeQuiz from '../components/quiz/ConfiguredPracticeQuiz';

// Helper function to check if a unit is accessible based on enrollment date
const isUnitAccessible = (unitIndex: number, enrollmentDate: string, isOpenToAll: boolean): { accessible: boolean; message?: string; daysLeft?: number } => {
  if (isOpenToAll) {
    return { accessible: true };
  }

  if (!enrollmentDate) {
    return { accessible: false, message: 'تاريخ التسجيل غير متوفر' };
  }

  const enrollment = new Date(enrollmentDate);
  const now = new Date();
  const daysSinceEnrollment = Math.floor((now.getTime() - enrollment.getTime()) / (1000 * 60 * 60 * 24));
  
  const unitStartDay = unitIndex * 7;
  const unitEndDay = (unitIndex + 1) * 7;
  
  if (daysSinceEnrollment >= unitStartDay && daysSinceEnrollment < unitEndDay) {
    const daysLeft = unitEndDay - daysSinceEnrollment;
    return { accessible: true, daysLeft };
  }
  
  if (daysSinceEnrollment < unitStartDay) {
    const daysUntilStart = unitStartDay - daysSinceEnrollment;
    return { 
      accessible: false, 
      message: `ستصبح هذه الوحدة متاحة خلال ${daysUntilStart} ${daysUntilStart === 1 ? 'يوم' : 'أيام'}` 
    };
  }
  
  return { 
    accessible: false, 
    message: 'انتهت فترة الوصول لهذه الوحدة' 
  };
};

const getUnitAccessStatus = (unitIndex: number, enrollmentDate: string, isOpenToAll: boolean, courseData: CourseDetails | null) => {
  const status = isUnitAccessible(unitIndex, enrollmentDate, isOpenToAll);
  
  if (status.accessible && status.daysLeft !== undefined) {
    return {
      ...status,
      statusText: `متاحة (${status.daysLeft} ${status.daysLeft === 1 ? 'يوم متبقي' : 'أيام متبقية'})`,
      statusColor: status.daysLeft <= 2 ? 'text-orange-600' : 'text-green-600'
    };
  }
  
  if (status.accessible && isOpenToAll) {
    return {
      ...status,
      statusText: 'متاحة دائماً',
      statusColor: 'text-green-600'
    };
  }
  
  return {
    ...status,
    statusText: status.message || 'غير متاحة',
    statusColor: 'text-red-600'
  };
};

const CourseCurriculum: React.FC = () => {
  useScrollToTop();
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { fetchCourseDetails, isLoading } = useCourseApi();
  const [courseDetails, setCourseDetails] = useState<CourseDetails | null>(null);
  const [collapsedUnits, setCollapsedUnits] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);

  // Load once on mount / courseId change
  useEffect(() => {
    if (!courseId) return;

    let cancelled = false;

    const loadCourseDetails = async () => {
      try {
        setInitialLoading(true);
        setError(null);
        const response = await fetchCourseDetails(courseId, true);
        if (cancelled) return;

        if (response?.success) {
          setCourseDetails(response.data);
        } else {
          setError(
            response?.message ||
              (response === null ? 'حدث خطأ أثناء تحميل الدورة' : 'Course details not found.')
          );
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading course:', err);
        setError('حدث خطأ أثناء تحميل الدورة');
      } finally {
        if (!cancelled) {
          setInitialLoading(false);
        }
      }
    };

    loadCourseDetails();

    return () => {
      cancelled = true;
    };
  }, [courseId, fetchCourseDetails]);

  const toggleUnit = (unitId: string) => {
    setCollapsedUnits((prev) =>
      prev.includes(unitId) ? prev.filter((id) => id !== unitId) : [...prev, unitId]
    );
  };

  const handleLessonClick = (unitIndex: number, lessonIndex: number) => {
    if (!courseDetails) {
      console.error('No course details available');
      return;
    }

    // Check if unit is accessible
    const unitAccess = isUnitAccessible(unitIndex, courseDetails.enrollmentDate, courseDetails.isOpenToAll);
    if (!unitAccess.accessible) {
      setError(unitAccess.message || 'هذه الوحدة غير متاحة حالياً');
      return;
    }

    const unit = courseDetails.units[unitIndex];
    if (!unit || !unit.lessons || lessonIndex >= unit.lessons.length) {
      console.error('Invalid unit or lesson index:', { unitIndex, lessonIndex, unit });
      setError('الدرس غير متوفر');
      return;
    }

    const lesson = unit.lessons[lessonIndex];
    console.log('Selected lesson:', lesson);
    
    // Check if assignment is already submitted — still allow opening to review
    setError(null);
    
    // Navigate to course player with query params (matching current project pattern)
    navigate(
      `/course-player/${courseId}?unit=${unitIndex}&lesson=${lessonIndex}&lessonId=${lesson.id}`
    );
  };

  const calculateProgress = () => {
    if (!courseDetails || !courseDetails.units) return 0;
    
    let totalLessons = 0;
    let completedLessons = 0;
    
    courseDetails.units.forEach(unit => {
      unit.lessons.forEach(lesson => {
        totalLessons++;
        if ((lesson.type === 'Video' && lesson.isCompleted) || 
            (lesson.type === 'Quiz' && lesson.isQuizSubmitted) ||
            (lesson.type === 'Assignment' && lesson.isAssignmentSubmitted) ||
            (lesson.type === 'File' && lesson.isFileOpened)) {
          completedLessons++;
        }
      });
    });
    
    return totalLessons > 0 ? (completedLessons / totalLessons) * 100 : 0;
  };

  // Show initial loading screen when page is first opened
  if (initialLoading || (isLoading && !courseDetails)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper px-4 font-cairo" dir="rtl">
        <div className="w-full max-w-sm p-4 text-center sm:p-8">
          <div className="relative mx-auto mb-5 h-14 w-14 sm:mb-6 sm:h-16 sm:w-16">
            <div className="absolute inset-0 animate-spin rounded-full border-4 border-green-100 border-t-green-700" />
          </div>
          <h3 className={`mb-2 text-lg font-bold sm:text-xl ${cardTheme.title}`}>جاري تحميل المنهج</h3>
          <p className={`text-sm sm:text-base ${cardTheme.subtitle}`}>يرجى الانتظار بينما نحمّل محتوى الدورة...</p>
        </div>
      </div>
    );
  }

  if (error && !courseDetails) {
    const ui = mapCourseLoadError(
      !courseId ? 'missing course id' : error
    );
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper px-4 font-cairo" dir="rtl">
        <div className={`${cardTheme.shell} w-full max-w-md p-6 text-center sm:p-8`}>
          <div className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl ${cardTheme.iconBox}`}>
            <BookOpen className="h-7 w-7" />
          </div>
          <h2 className={`mb-2 text-xl font-bold sm:text-2xl ${cardTheme.title}`}>{ui.title}</h2>
          <p className={`mb-6 text-sm sm:text-base ${cardTheme.subtitle}`}>{ui.message}</p>
          <button
            type="button"
            onClick={() => navigate('/student-portal')}
            className={`inline-flex min-h-11 w-full items-center justify-center rounded-xl px-6 py-3 text-sm font-bold sm:w-auto sm:text-base ${cardTheme.btnPrimary}`}
          >
            {ui.buttonLabel}
          </button>
        </div>
      </div>
    );
  }

  if (!courseDetails) {
    const ui = mapCourseLoadError('Course details not found.');
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper px-4 font-cairo" dir="rtl">
        <div className={`${cardTheme.shell} w-full max-w-md p-6 text-center sm:p-8`}>
          <h2 className={`mb-2 text-xl font-bold sm:text-2xl ${cardTheme.title}`}>{ui.title}</h2>
          <p className={`mb-6 text-sm sm:text-base ${cardTheme.subtitle}`}>{ui.message}</p>
          <button
            type="button"
            onClick={() => navigate('/student-portal')}
            className={`inline-flex min-h-11 w-full items-center justify-center rounded-xl px-6 py-3 text-sm font-bold sm:w-auto sm:text-base ${cardTheme.btnPrimary}`}
          >
            {ui.buttonLabel}
          </button>
        </div>
      </div>
    );
  }

  const totalLessons = courseDetails
    ? courseDetails.units.reduce((total, unit) => total + unit.lessons.length, 0)
    : 0;
  const progressPct = calculateProgress();

  const availableQuizzes = courseDetails
    ? courseDetails.units.flatMap((unit) =>
        unit.lessons
          .filter((lesson) => String(lesson.type).toLowerCase() === 'quiz')
          .map((lesson) => ({
            id: lesson.id,
            lessonName: lesson.lessonName,
            unitName: unit.unitName,
          }))
      )
    : [];

  const lessonTypeMeta = (type: string) => {
    if (type === 'Video') return { label: 'فيديو', icon: videoLessonIcon };
    if (type === 'Quiz') return { label: 'اختبار', icon: quizLessonIcon };
    if (type === 'Assignment') return { label: 'واجب', icon: homeworkLessonIcon };
    if (type === 'File') return { label: 'ملف', icon: pdfLessonIcon };
    return { label: type, icon: videoLessonIcon };
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-paper font-cairo" dir="rtl">
      <header className="sticky top-0 z-20 border-b border-green-100/80 bg-white/95 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-4xl items-center px-3 sm:px-6">
          <button
            type="button"
            onClick={() => navigate('/student-portal')}
            className="inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-green-800 hover:text-green-600"
          >
            <ArrowRight className="h-5 w-5" />
            كورساتي وحصصي
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-3 py-4 pb-24 sm:px-6 sm:py-8 sm:pb-10">
        {courseDetails && (
          <section className={`${cardTheme.shell} mb-5 overflow-hidden sm:mb-7`}>
            <div className={`${cardTheme.headerBanner} px-4 py-5 sm:px-6 sm:py-6`}>
              <div className={`mb-3 inline-flex ${cardTheme.badgeGold} bg-gold-500/20 text-gold-200 border-gold-400/40`}>
                <BookOpen className="h-3.5 w-3.5" />
                منهج الدورة
              </div>
              <h1 className="mb-2 break-words text-xl font-bold leading-snug text-white sm:text-2xl lg:text-3xl">
                {courseDetails.courseName}
              </h1>
              <p className="mb-4 text-sm text-green-100/85 sm:text-base">
                اختر الدرس وابدأ التعلم — تقدّمك محفوظ تلقائياً
              </p>

              <div className="mb-3 h-2.5 overflow-hidden rounded-full bg-white/15">
                <div
                  className="h-full rounded-full bg-gold-400 transition-all duration-700"
                  style={{ width: `${progressPct}%` }}
                />
              </div>

              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-semibold text-white sm:text-xs">
                  <BookOpen className="h-3.5 w-3.5" />
                  {courseDetails.units.length} وحدات
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-semibold text-white sm:text-xs">
                  <PlayCircle className="h-3.5 w-3.5" />
                  {totalLessons} دروس
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-gold-500/25 px-2.5 py-1 text-[11px] font-semibold text-gold-200 sm:text-xs">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  {progressPct.toFixed(0)}% مكتمل
                </span>
              </div>
            </div>
          </section>
        )}

        {courseDetails && (
          <ConfiguredPracticeQuiz quizzes={availableQuizzes} />
        )}

        {courseDetails && !courseDetails.isOpenToAll && (
          <div className="mb-5 sm:mb-7">
            {courseDetails.units.map((unit, unitIndex) => {
              const unitAccess = getUnitAccessStatus(
                unitIndex,
                courseDetails.enrollmentDate,
                courseDetails.isOpenToAll,
                courseDetails
              );
              if (unitAccess.accessible && unitAccess.daysLeft) {
                return (
                  <UnitAccessTimer
                    key={unit.id}
                    unitIndex={unitIndex}
                    enrollmentDate={courseDetails.enrollmentDate}
                    totalUnits={courseDetails.units.length}
                    className="mb-3 sm:mb-4"
                  />
                );
              }
              return null;
            })}
          </div>
        )}

        {error && (
          <div className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700 sm:mb-5">
            <X className="mt-0.5 h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1 break-words">{error}</span>
            <button
              type="button"
              onClick={() => setError(null)}
              className="inline-flex min-h-10 min-w-10 shrink-0 items-center justify-center"
              aria-label="إغلاق"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {courseDetails && (
          <div className="space-y-3 sm:space-y-4">
            <div className="mb-1 flex items-center justify-between gap-2 px-0.5">
              <h2 className={`text-base font-bold sm:text-lg ${cardTheme.title}`}>محتوى الوحدات</h2>
              <span className={cardTheme.badge}>{courseDetails.units.length} وحدة</span>
            </div>

            {courseDetails.units.map((unit, unitIndex) => {
              const unitAccess = getUnitAccessStatus(
                unitIndex,
                courseDetails.enrollmentDate,
                courseDetails.isOpenToAll,
                courseDetails
              );
              const isUnitLocked = !unitAccess.accessible;
              const isCollapsed = collapsedUnits.includes(unit.id.toString());
              const completedInUnit = unit.lessons.filter(
                (lesson) =>
                  (lesson.type === 'Video' && lesson.isCompleted) ||
                  (lesson.type === 'Quiz' && lesson.isQuizSubmitted) ||
                  (lesson.type === 'Assignment' && lesson.isAssignmentSubmitted) ||
                  (lesson.type === 'File' && lesson.isFileOpened)
              ).length;

              return (
                <div
                  key={unit.id}
                  className={`${cardTheme.shell} ${!isUnitLocked ? cardTheme.shellHover : 'opacity-70'}`}
                >
                  <button
                    type="button"
                    className={`flex w-full min-h-14 items-start gap-3 p-3.5 text-right sm:items-center sm:gap-4 sm:p-5 ${
                      isUnitLocked ? 'cursor-not-allowed bg-gray-50/80' : ''
                    }`}
                    onClick={() => !isUnitLocked && toggleUnit(unit.id.toString())}
                    disabled={isUnitLocked}
                  >
                    <div
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl sm:h-12 sm:w-12 ${
                        isUnitLocked ? 'bg-gray-200 text-gray-500' : cardTheme.iconBox
                      }`}
                    >
                      {isUnitLocked ? (
                        <Lock className="h-5 w-5" />
                      ) : (
                        <span className="text-base font-bold sm:text-lg">{unitIndex + 1}</span>
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <h3
                        className={`mb-1 break-words text-sm font-bold leading-snug sm:text-base lg:text-lg ${
                          isUnitLocked ? 'text-gray-500' : cardTheme.title
                        }`}
                      >
                        {unit.unitName}
                      </h3>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`text-[11px] font-semibold sm:text-xs ${unitAccess.statusColor}`}>
                          {unitAccess.statusText}
                        </span>
                        <span className="text-[11px] text-gray-500 sm:text-xs">
                          {completedInUnit}/{unit.lessons.length} دروس
                        </span>
                      </div>
                      {!isUnitLocked && unit.lessons.length > 0 && (
                        <div className="mt-2 h-1.5 max-w-[12rem] overflow-hidden rounded-full bg-green-100">
                          <div
                            className="h-full rounded-full bg-gold-500"
                            style={{
                              width: `${(completedInUnit / unit.lessons.length) * 100}%`,
                            }}
                          />
                        </div>
                      )}
                    </div>

                    {!isUnitLocked && (
                      <ChevronDown
                        className={`mt-1 h-5 w-5 shrink-0 text-green-700 transition-transform sm:mt-0 ${
                          isCollapsed ? '' : 'rotate-180'
                        }`}
                      />
                    )}
                  </button>

                  {!isUnitLocked && !isCollapsed && (
                    <div className="border-t border-green-100 bg-gradient-to-b from-green-50/40 to-white">
                      {unit.lessons.length === 0 ? (
                        <div className="px-4 py-6 text-center sm:px-6">
                          <p className={`text-sm font-semibold ${cardTheme.title}`}>لسه مفيش دروس هنا</p>
                          <p className={`mt-1 text-xs sm:text-sm ${cardTheme.subtitle}`}>
                            الوحدة دي موجودة، بس المدرس لسه مضافش دروس جواها.
                          </p>
                        </div>
                      ) : (
                      unit.lessons.map((lesson, lessonIndex) => {
                        const isCompleted =
                          (lesson.type === 'Video' && lesson.isCompleted) ||
                          (lesson.type === 'Quiz' && lesson.isQuizSubmitted) ||
                          (lesson.type === 'Assignment' && lesson.isAssignmentSubmitted) ||
                          (lesson.type === 'File' && lesson.isFileOpened);
                        const isQuizSubmitted = lesson.type === 'Quiz' && lesson.isQuizSubmitted === true;
                        const isAssignmentSubmitted =
                          lesson.type === 'Assignment' && lesson.isAssignmentSubmitted === true;
                        const isFileOpened = lesson.type === 'File' && lesson.isFileOpened === true;
                        const { label, icon } = lessonTypeMeta(lesson.type);

                        return (
                          <button
                            key={lesson.id}
                            type="button"
                            className="flex w-full min-h-14 items-start gap-3 border-b border-green-50 p-3.5 text-right last:border-b-0 hover:bg-white/80 sm:items-center sm:gap-4 sm:p-4"
                            onClick={() => handleLessonClick(unitIndex, lessonIndex)}
                          >
                            <div
                              className={`relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl sm:h-12 sm:w-12 ${
                                isCompleted
                                  ? 'bg-green-50 ring-2 ring-green-600/30'
                                  : 'bg-white border border-green-100 shadow-sm'
                              }`}
                            >
                              <img
                                src={icon}
                                alt={label}
                                className="h-7 w-7 object-contain sm:h-8 sm:w-8"
                                loading="lazy"
                              />
                              {isCompleted && (
                                <span className="absolute -bottom-1 -left-1 flex h-5 w-5 items-center justify-center rounded-full bg-green-700 text-white shadow">
                                  <CheckCircle2 className="h-3.5 w-3.5" />
                                </span>
                              )}
                            </div>

                            <div className="min-w-0 flex-1">
                              <h4 className="mb-1 break-words text-sm font-bold leading-snug text-green-900 sm:text-base">
                                {lesson.lessonName}
                              </h4>
                              <div className="flex flex-wrap items-center gap-1.5">
                                <span
                                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold sm:text-[11px] ${
                                    lesson.type === 'Quiz'
                                      ? 'bg-gold-50 text-gold-800 border border-gold-200'
                                      : lesson.type === 'Assignment'
                                        ? 'bg-amber-50 text-amber-900 border border-amber-200'
                                        : 'bg-green-50 text-green-800 border border-green-100'
                                  }`}
                                >
                                  {label}
                                </span>
                                {isCompleted && (
                                  <span className="text-[10px] font-semibold text-green-700 sm:text-[11px]">
                                    مكتمل
                                  </span>
                                )}
                                {(isQuizSubmitted || isAssignmentSubmitted) && !isCompleted && (
                                  <span className="text-[10px] font-semibold text-green-700 sm:text-[11px]">
                                    تم الإرسال
                                  </span>
                                )}
                                {isFileOpened && !isCompleted && (
                                  <span className="text-[10px] font-semibold text-gold-700 sm:text-[11px]">
                                    تم الفتح
                                  </span>
                                )}
                              </div>
                            </div>

                            <ChevronLeft className="mt-2 h-4 w-4 shrink-0 text-gray-400 sm:mt-0 sm:h-5 sm:w-5" />
                          </button>
                        );
                      })
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseCurriculum;
